import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { useDispatch, useSelector } from 'react-redux';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import Typography from '@material-ui/core/Typography';
import { t } from '@configuratorware/configurator-frontendgui/Framework/i18n';
import get from 'lodash/get';
import range from 'lodash/range';
import flatten from 'lodash/flatten';
import isString from 'lodash/isString';
import orderBy from 'lodash/orderBy';
import uniq from 'lodash/uniq';
import moment from 'moment';
import {
  saveSeatData,
  saveSeatTypeData,
  selectAnswer,
  setQuestions,
  setSelectedDesign,
} from '../../Reducers/Configurator/Actions';
import { PRODUCT } from '../../Constants/Steps';
import { Button, TextField, useMediaQuery, useTheme } from '@material-ui/core';
import clsx from 'clsx';
import interiorImage1 from './res/interior1.jpg';
import interiorImage2 from './res/interior2.jpg';
import getImageUrl from '@configuratorware/configurator-frontendgui/App/Utils/getImageUrl';
import { useRef } from 'react';
import { ALLSEATS } from '../../Constants/SeatPositions';
import designTypes from '../../Constants/DesignTypes';
import Abbreviations from '../Abbreviations/Abbreviations';
import BlurTextField from './BlurTextField';

const useStyles = makeStyles((theme) => {
  return {
    tabContent: {
      margin: '32px 0px 16px 32px',
      width: '100%',
      [theme.breakpoints.down('sm')]: {
        height: 'auto',
        flex: 1,
      },
      [theme.breakpoints.down('xs')]: {
        margin: 0,
        maxWidth: '100%',
      },
      display: 'flex',
      flexDirection: 'column',
    },
    rightContainer: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      [theme.breakpoints.down('sm')]: {
        width: 'calc(100% - 300px)',
      },
    },
    productImageContainer: {
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      '& > div': {
        flex: 1,
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'auto 100%',
        margin: theme.spacing(1, 0),
      },
    },
    image1: {
      backgroundImage: `url(${interiorImage1})`,
    },
    image2: {
      backgroundImage: `url(${interiorImage2})`,
    },
    tabContainer: {
      display: 'flex',
      flex: 1,
      overflow: 'hidden',
    },
    title: {
      fontStyle: 'italic',
      margin: '16px 0px 32px',
      [theme.breakpoints.up('md')]: {
        marginRight: 96,
      },
      [theme.breakpoints.down('xs')]: {
        fontSize: '0.8rem',
        margin: theme.spacing(2, 0),
      },
    },
    titleMessage: {
      fontWeight: 'bold',
      marginBottom: 0,
    },
    preselectionNoteContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: 20,
      width: '50%',
      height: '100%',
      [theme.breakpoints.down('xs')]: {
        width: '100%',
        maxWidth: '100%',
        marginRight: 0,
        paddingRight: 0,
      },
      [theme.breakpoints.up('md')]: {
        paddingRight: 24,
      },
    },
    scrollableArea: {
      overflowY: 'auto',
      overflowX: 'hidden',
      flex: 1,
      position: 'relative',
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
    },
    questionsContainer: {
      flexBasis: '50%',
      paddingRight: 16,
      [theme.breakpoints.up('md')]: {
        paddingRight: 24,
      },
      [theme.breakpoints.down('sm')]: {
        paddingRight: 16,
        width: 'auto',
      },
    },
    question: {
      margin: '10px 0px',
      [theme.breakpoints.down('xs')]: {},
      [theme.breakpoints.up('md')]: {
        display: 'flex',
        alignItems: 'center',
      },
    },
    questionTitle: {
      margin: '10px 0px',
      paddingRight: theme.spacing(1),
      [theme.breakpoints.up('md')]: {
        flex: 1,
      },
      [theme.breakpoints.down('xs')]: {
        maxWidth: '100%',
      },
    },
    answerSelector: {
      padding: '15px 32px 15px 10px',
      '&:focus': {
        backgroundColor: '#ffffff',
      },
    },
    selectorOutline: {
      width: '100%',
      [theme.breakpoints.up('md')]: {
        width: '50%',
        maxWidth: 330,
      },
    },
    yearDataFields: {
      display: 'flex',
      gap: 20,
      [theme.breakpoints.up('md')]: {
        width: '50%',
      },
      maxWidth: 'min(100%, 330px)',
      '& .yearfield': {
        width: 'calc(50% - 10px)',
      },
    },
    selectedAnswer: {
      fontWeight: 'bold',
      maxWidth: 330,
      width: '50%',
      margin: '16px 0px',
      [theme.breakpoints.down('xs')]: {
        width: '100%',
      },
    },
    preselectionNote: {
      color: theme.palette.primary.main,
    },
    mobilePreselectionNote: {
      color: theme.palette.primary.main,
      fontSize: '0.7rem',
    },
    seatQuestionTitle: {
      fontWeight: 'bold',
      maxWidth: 300,
      margin: theme.spacing(6, 0, 4, 0),
      fontSize: '1.1rem',
      [theme.breakpoints.up('md')]: {
        maxWidth: 600,
      },
    },
    mainButton: {
      margin: 0,
      padding: '6px 15px',
      height: 50,
      borderRadius: 0,
      lineHeight: 1.5,
      width: 150,
      backgroundColor: '#818181',
      color: '#ffffff',
      '&.MuiButton-contained.Mui-disabled': {
        backgroundColor: '#818181',
        opacity: 0.4,
      },
      '&:hover': {
        backgroundColor: '#818181',
        cursor: 'pointer',
      },
      '@media (hover: none)': {
        '&:hover': {
          backgroundColor: '#818181',
          opacity: 1,
        },
      },
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
    },
    mainButtonLabel: {
      color: 'white',
      fontSize: 15,
      fontWeight: 'bold',
      textTransform: 'none',
      whiteSpace: 'nowrap',
      [theme.breakpoints.down('xs')]: {
        whiteSpace: 'normal',
      },
    },
    abbreviationsMobile: {
      marginBottom: theme.spacing(2),
      fontSize: '0.8rem',
    },
    designsContainer: ({ isMinHeight }) => ({
      flex: 1,
      display: 'flex',
      flexDirection: isMinHeight ? 'column' : 'row',
      justifyContent: 'center',
      alignItems: 'center',

      [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
      },
      [theme.breakpoints.down('xs')]: {
        alignItems: 'center',
        width: '100%',
        justifyContent: 'space-between',
      },
    }),
    designImage: {
      flex: 1,
      margin: theme.spacing(1),
      width: '100%',
      maxWidth: 318,
      backgroundSize: 'contain',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
      maxHeight: 500,
      [theme.breakpoints.down('sm')]: {
        height: '100%',
      },
    },
    selectedDesignImage: {
      borderColor: '#d8d8d8',
    },
    designButton: {
      height: 100,
      alignItems: 'center',
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
      [theme.breakpoints.down('sm')]: {
        flex: 1,
        padding: theme.spacing(2),
        height: 'auto',
      },
    },
    selectedDesignButton: {
      backgroundColor: '#d8d8d8',
    },
    designContainer: {
      flex: 1,
      marginRight: 60,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      cursor: 'pointer',
      height: '100%',
      justifyContent: 'center',
      [theme.breakpoints.down('sm')]: {
        width: '100%',
        flexDirection: 'row',
        flex: 1,
        margin: 0,
      },
      [theme.breakpoints.up('md')]: {
        maxWidth: 318,
      },
      '&$disabled': {
        opacity: 0.5,
        cursor: 'default',
      },
    },
    designTabContent: {
      flex: 1,
      display: 'flex',
      justifyContent: 'center',

      [theme.breakpoints.down('sm')]: {
        height: 'calc(100% - 60px)',
        overflowY: 'auto',
      },
      [theme.breakpoints.down('xs')]: {
        justifyContent: 'flex-start',
      },
    },
    disabled: {},
    input: {
      ...theme.typography.body2,
    },
  };
});

const getGeneralQuestions = (questions) => {
  const generalQuestions = [];
  for (const q of questions) {
    generalQuestions.push(q);
    if (q.identifier === 'seat_position') {
      break;
    }
  }
  return generalQuestions;
};

const QuestionContainer = (props) => {
  const [isMounted, setIsMounted] = useState(false);
  const handleMount = (ref) => {
    if (ref && !isMounted) {
      setIsMounted(true);
      const { offsetParent } = ref;
      const elementHeight =
        ref.offsetHeight + parseInt(window.getComputedStyle(ref).getPropertyValue('margin-bottom'));
      if (ref.offsetTop + elementHeight > offsetParent.clientHeight + offsetParent.scrollTop) {
        offsetParent.scrollTop = ref.offsetTop - offsetParent.clientHeight + elementHeight;
      } else if (ref.offsetTop < offsetParent.scrollTop) {
        offsetParent.scrollTop = ref.offsetTop;
      }
    }
  };
  return <div {...props} ref={handleMount} />;
};

export const StepTabContent = ({ selectedStage }) => {
  const {
    questions,
    selectedDesign,
    clearYearData,
    availableDesigns,
    configuration: { customdata },
  } = useSelector((state) => state.configurator);
  const dispatch = useDispatch();
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const questionContainerRef = useRef();
  const isSmallHeight = useMediaQuery('(max-height:840px)');
  const isMinHeight = useMediaQuery('(max-height:660px)');
  const classes = useStyles({ isSmallHeight, isMinHeight });
  const filteredQuestions = questions.filter((ques) => ques.identifier !== 'design');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
  const currentSeatType = questions.find((question) => question.identifier === 'seat_position');
  const selectedSeatPostion = get(customdata, 'seatData', []).find((s) => s.identifier === 'seat_position');
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();
  const [hasPreselection, setHasPreselection] = useState(false);

  useEffect(() => {
    if (clearYearData) {
      setSelectedMonth(null);
      setSelectedYear(null);
    }
  }, [clearYearData]);

  useEffect(() => {
    if (!questions) return;

    questions.forEach((q) => {
      if (q.identifier === 'year_start' && q.answers.length) {
        let selectedAnswerMonth = get(q, 'selectedAnswer');
        selectedAnswerMonth =
          selectedAnswerMonth && isString(selectedAnswerMonth)
            ? selectedAnswerMonth.split('/')[0]
            : selectedMonth;
        if (selectedAnswerMonth) {
          setSelectedMonth(parseInt(selectedAnswerMonth));
        }
      }
    });
  }, [questions]);

  useEffect(() => {
    questions
      .filter((q) => ['article_type', 'year_start'].indexOf(q.identifier) === -1)
      .some((q) => get(q, 'answers.length') === 1) && setHasPreselection(true);
  }, [questions]);

  const onAnswerSelection = (question, e) => {
    const id = question.identifier;
    let answer = e.target.value;

    if (id === 'seat_position') {
      const filteredAnswers = currentSeatType.answers.filter((ans) => ans !== ALLSEATS);
      const value = e.target.value === ALLSEATS ? filteredAnswers[0] : answer;
      dispatch(saveSeatData({ ...question, selectedAnswer: answer }));
      answer = value;
    } else if (currentSeatType && currentSeatType.selectedAnswer) {
      dispatch(
        saveSeatTypeData(currentSeatType.selectedAnswer, {
          ...question,
          selectedAnswer: answer,
        })
      );
    }
    // when other seatType related question is updated, questions tree need to be updated and sent to
    // processQuestions call in order to get  next correct question
    if (currentSeatType && question.seatType && currentSeatType.selectedAnswer !== question.seatType) {
      const seatTypeQuestions = get(customdata, 'seatData', []).find(
        (s) => s.identifier === question.seatType
      ).questions;
      const index = questions.findIndex((q) => q.identifier === 'seat_position');
      // current/last seatType questions were removed from questions array and selectedSeatType questions need to be added to array
      const updatedQuestions = questions.splice(0, index + 1);
      dispatch(
        setQuestions(
          updatedQuestions
            .map((qu) => {
              if (qu.identifier === 'seat_position') {
                return { ...qu, selectedAnswer: question.seatType };
              }
              return qu;
            })
            .concat(seatTypeQuestions)
        )
      );
    }
    dispatch(selectAnswer(question.identifier, answer));
  };

  const getMonths = (selectedYear, yearMonthsMap) => {
    const months = [];
    yearMonthsMap.filter((yr) => {
      if (yr.year.toString() === selectedYear.toString()) {
        months.push(yr.months);
      }
    });
    return flatten(months);
  };

  const validateYear = (question, value, yearMonthsMap) => {
    setSelectedYear(value);
    let updatedMonth = selectedMonth;
    const checkMonths = getMonths(value, yearMonthsMap);
    if (value === currentYear && selectedMonth && selectedMonth > currentMonth + 1) {
      updatedMonth = currentMonth + 1;
      setSelectedMonth(updatedMonth);
    } else if (
      checkMonths &&
      checkMonths.length > 0 &&
      selectedMonth &&
      !checkMonths.includes(selectedMonth)
    ) {
      updatedMonth = checkMonths[0];
      setSelectedMonth(updatedMonth);
    }

    if (updatedMonth && value) {
      dispatch(selectAnswer(question.identifier, updatedMonth + '/' + value));
    }
  };

  const renderYear = (question) => {
    const yearArr = [];
    const yearMonthsMap = [];
    question.answers.map((ans) => {
      let timeStamps = ans.split('|');
      timeStamps = timeStamps.map((time, i) => {
        const year = moment.unix(time).format('YYYY');
        if (time === 0) {
          yearMonthsMap.push({ year: 1995, months: range(1, 13, 1) });
          return 1995;
        } else if (year > moment().format('YYYY')) {
          yearMonthsMap.push({ year: moment().format('YYYY'), months: range(1, currentMonth + 2, 1) });
          return moment().format('YYYY');
        } else {
          yearMonthsMap.push({
            year: year,
            months:
              i === 0
                ? range(moment.unix(time).format('MM'), 13, 1)
                : range(1, Number(moment.unix(time).format('MM')) + 1, 1),
          });
          return year;
        }
      });
      yearArr.push(range(Number(timeStamps[0]), Number(timeStamps[1]) + 1, 1));
    });

    let selectedAnswerMonth = get(question, 'selectedAnswer');
    selectedAnswerMonth =
      selectedAnswerMonth && isString(selectedAnswerMonth)
        ? selectedAnswerMonth.split('/')[0]
        : selectedMonth;
    let selectedAnswerYear = get(question, 'selectedAnswer');
    selectedAnswerYear =
      selectedAnswerYear && isString(selectedAnswerYear) ? selectedAnswerYear.split('/')[1] : selectedYear;

    return (
      <>
        <Select
          className={clsx(classes.selectorOutline, 'yearfield')}
          classes={{
            root: classes.answerSelector,
            notchedOutline: classes.selectorNotchedOutline,
          }}
          autoFocus
          variant="outlined"
          value={selectedAnswerMonth || 'default'}
          onChange={(e) => {
            setSelectedMonth(e.target.value);
            if (e.target.value && selectedYear) {
              dispatch(selectAnswer(question.identifier, e.target.value + '/' + selectedYear));
            }
          }}
        >
          <MenuItem className={classes.MenuItemOption} key={'default'} value={'default'}>
            <Typography className={classes.toolTitle} noWrap variant="body2">
              {t('pleaseSelect')}
            </Typography>
          </MenuItem>
          {selectedYear && yearMonthsMap.find((yr) => yr.year.toString() === selectedYear.toString())
            ? getMonths(selectedYear, yearMonthsMap).map((month) => (
                <MenuItem className={classes.MenuItemOption} key={month} value={month}>
                  <Typography className={classes.toolTitle} noWrap variant="body2">
                    {month}
                  </Typography>
                </MenuItem>
              ))
            : addMenuItem(1, 12)}
        </Select>

        <Select
          className={clsx(classes.selectorOutline, 'yearfield')}
          classes={{
            root: classes.answerSelector,
            notchedOutline: classes.selectorNotchedOutline,
          }}
          autoFocus
          variant="outlined"
          value={selectedAnswerYear || 'default'}
          onChange={(e) => {
            validateYear(question, e.target.value, yearMonthsMap);
          }}
        >
          <MenuItem className={classes.MenuItemOption} key={'default'} value={'default'}>
            <Typography className={classes.toolTitle} noWrap variant="body2">
              {t('pleaseSelect')}
            </Typography>
          </MenuItem>
          {orderBy(uniq(flatten(yearArr))).map((i) => (
            <MenuItem className={classes.MenuItemOption} key={i} value={i}>
              <Typography className={classes.toolTitle} noWrap variant="body2">
                {i}
              </Typography>
            </MenuItem>
          ))}
        </Select>
      </>
    );
  };

  const addMenuItem = (start, end) => {
    let items = [];
    for (let i = start; i <= end; i++) {
      items.push(
        <MenuItem className={classes.MenuItemOption} key={i} value={i}>
          <Typography className={classes.toolTitle} noWrap variant="body2">
            {i}
          </Typography>
        </MenuItem>
      );
    }
    return items;
  };

  const renderPreselectionAsterisk = () => {
    return <span className={classes.preselectionNote}> *</span>;
  };

  const renderPreselectionNote = () => {
    return !isMobile ? (
      <Typography className={classes.preselectionNote}>{t('preselectionNote')}</Typography>
    ) : (
      <Typography className={classes.mobilePreselectionNote}>{t('preselectionNote')}</Typography>
    );
  };

  const renderSeatQuestions = () => {
    const seatTypeInformation = get(customdata, 'seatData', []).filter(
      (s) => s.identifier !== 'seat_position'
    );
    return (
      seatTypeInformation.length > 0 &&
      seatTypeInformation.map((seatType, i) => (
        <React.Fragment key={seatType.identifier}>
          {renderSeatPositionQuestion({ ...seatType.questions[0], seatType: seatType.identifier })}
          {seatType.questions.map(
            (question) =>
              question &&
              question.identifier !== 'design' &&
              renderQuestion({ ...question, seatType: seatType.identifier }, i)
          )}
        </React.Fragment>
      ))
    );
  };

  const renderSeatPositionQuestion = (question) => {
    return (
      <Typography className={classes.seatQuestionTitle}>
        {t('stepTabContent.seatPositionQuestion') +
          ' ' +
          get(question, 'seat_type', question.seatType ? question.seatType : currentSeatType.selectedAnswer)}
      </Typography>
    );
  };

  const renderQuestion = (question, i) => {
    return question.answers.length === 1 && question.identifier !== 'year_start' ? (
      question.identifier === 'arm_rest_available' || question.identifier === 'kst_available' ? (
        !question.answers[0] ? (
          <Typography className={classes.selectedAnswer} variant="body2">
            {question.identifier === 'arm_rest_available' ? t('armrest_noCovers') : t('headrest_noCovers')}
          </Typography>
        ) : (
          <QuestionContainer className={classes.question} key={question.identifier}>
            <Typography className={classes.questionTitle} variant="body2">
              {question.title}
            </Typography>
            <Select
              className={classes.selectorOutline}
              classes={{
                root: classes.answerSelector,
                notchedOutline: classes.selectorNotchedOutline,
              }}
              autoFocus
              variant="outlined"
              value={get(question, 'selectedAnswer', 'default')}
              onChange={(e) => {
                currentSeatType &&
                  currentSeatType.selectedAnswer &&
                  dispatch(
                    saveSeatTypeData(currentSeatType.selectedAnswer, {
                      ...question,
                      selectedAnswer: e.target.value,
                    })
                  );
                dispatch(selectAnswer(question.identifier, e.target.value));
              }}
            >
              <MenuItem className={classes.MenuItemOption} value={'default'}>
                <Typography className={classes.toolTitle} noWrap variant="body2">
                  {t('pleaseSelect')}
                </Typography>
              </MenuItem>
              <MenuItem className={classes.MenuItemOption} value={1}>
                <Typography className={classes.toolTitle} noWrap variant="body2">
                  {t('yes')}
                </Typography>
              </MenuItem>
              <MenuItem className={classes.MenuItemOption} value={0}>
                <Typography className={classes.toolTitle} noWrap variant="body2">
                  {t('no')}
                </Typography>
              </MenuItem>
            </Select>
          </QuestionContainer>
        )
      ) : (
        <QuestionContainer className={classes.question} key={question.identifier}>
          <Typography className={classes.questionTitle} variant="body2">
            {question.title}
          </Typography>
          <Typography className={classes.selectedAnswer} variant="body2">
            {question.answers[0]}
            {question.identifier !== 'article_type' &&
              question.identifier !== 'year_start' &&
              renderPreselectionAsterisk()}
          </Typography>
        </QuestionContainer>
      )
    ) : question.type === 'input' ? (
      <QuestionContainer className={classes.question} key={question.identifier}>
        <Typography className={classes.questionTitle} variant="body2">
          {question.title}
        </Typography>
        <BlurTextField
          className={classes.selectorOutline}
          value={question.selectedAnswer}
          variant="outlined"
          onBlur={(e) => dispatch(selectAnswer(question.identifier, e.target.value))}
          InputProps={{
            classes: {
              input: classes.input,
            },
          }}
        />
      </QuestionContainer>
    ) : (
      <QuestionContainer className={classes.question} key={question.identifier}>
        <Typography className={classes.questionTitle} variant="body2">
          {question.title}
        </Typography>
        {question.identifier === 'year_start' ? (
          <div className={classes.yearDataFields}>{renderYear(question)}</div>
        ) : (
          <Select
            className={classes.selectorOutline}
            classes={{
              root: classes.answerSelector,
              notchedOutline: classes.selectorNotchedOutline,
            }}
            autoFocus
            variant="outlined"
            value={
              question.identifier === 'seat_position'
                ? get(selectedSeatPostion, 'selectedAnswer', 'default')
                : get(question, 'selectedAnswer', 'default')
            }
            onChange={(e) => {
              onAnswerSelection(question, e);
            }}
          >
            <MenuItem className={classes.MenuItemOption} key={'default'} value={'default'}>
              <Typography className={classes.toolTitle} noWrap variant="body2">
                {t('pleaseSelect')}
              </Typography>
            </MenuItem>
            {question.answers &&
              question.answers.map((answer, i) => (
                <MenuItem className={classes.MenuItemOption} key={i} value={answer}>
                  <Typography className={classes.toolTitle} noWrap variant="body2">
                    {answer}
                  </Typography>
                </MenuItem>
              ))}
          </Select>
        )}
      </QuestionContainer>
    );
  };

  return (
    <div className={classes.tabContent}>
      {selectedStage === PRODUCT ? (
        <>
          <Typography className={classes.title} variant="body1">
            {t('stepTabContent.productHeaderTitle')}
          </Typography>
          {isMobile && <Abbreviations className={classes.abbreviationsMobile} />}
          <div className={classes.tabContainer}>
            <div className={classes.preselectionNoteContainer}>
              <div className={classes.scrollableArea}>
                <div className={classes.questionsContainer} ref={questionContainerRef}>
                  {getGeneralQuestions(filteredQuestions).map((question, i) => (
                    <React.Fragment key={question.identifier}>
                      {renderQuestion(question, i)}
                      {question.identifier === 'seat_position' && renderSeatQuestions()}
                    </React.Fragment>
                  ))}
                </div>
              </div>
              {hasPreselection && renderPreselectionNote()}
            </div>
            {!isMobile && (
              <div className={classes.rightContainer}>
                <div className={classes.productImageContainer}>
                  <div className={classes.image1} />
                  <div className={classes.image2} />
                </div>
                <Abbreviations />
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          <Typography className={classes.title} variant="body1" component="div">
            {t('stepTabContent.designHeaderTitle')}
            <p className={classes.titleMessage}>{t('stepTabContent.designHeaderTitleMessage')}</p>
          </Typography>
          <div className={classes.designTabContent}>
            <div className={classes.designsContainer}>
              {designTypes.map((answer, i) => {
                const isAvailable = availableDesigns.indexOf(answer) !== -1;
                return (
                  <div
                    key={i}
                    className={clsx(classes.designContainer, !isAvailable && classes.disabled)}
                    onClick={() => isAvailable && dispatch(setSelectedDesign(answer))}
                  >
                    <div
                      className={clsx(
                        classes.designImage,
                        selectedDesign === answer && classes.selectedDesignImage
                      )}
                      style={{
                        backgroundImage: `url(${getImageUrl(
                          `/images/item/image/${answer.toLowerCase()}.jpg`
                        )})`,
                      }}
                    />
                    <div
                      className={clsx(
                        classes.designButton,
                        selectedDesign === answer && classes.selectedDesignButton
                      )}
                    >
                      <Button
                        variant="contained"
                        classes={{
                          root: classes.mainButton,
                          label: classes.mainButtonLabel,
                        }}
                        disabled={!isAvailable}
                      >
                        {answer}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

StepTabContent.propTypes = {
  selectedStage: PropTypes.string,
};

export default StepTabContent;
